import { type DynamicMockDefinition } from './plugins/msw-mocks/msw-mocks.types';

const STORAGE_KEY = '__acme_devtools__';

export interface DevToolsStorage {
  version: '0.0.1';
  app: 'b2b';
  panel: { x: number; y: number } | null;
  featureFlags: { overrides: Record<string, boolean> };
  mswMocks: { definitions: DynamicMockDefinition[] };
  plugins: Record<string, unknown>;
}

const DEFAULT_STATE: DevToolsStorage = {
  version: '0.0.1',
  app: 'b2b',
  panel: null,
  featureFlags: { overrides: {} },
  mswMocks: { definitions: [] },
  plugins: {},
};

export function loadState(): DevToolsStorage {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as Partial<DevToolsStorage>;
      return {
        ...DEFAULT_STATE,
        ...parsed,
        featureFlags: {
          ...DEFAULT_STATE.featureFlags,
          ...(parsed.featureFlags ?? {}),
        },
        mswMocks: {
          ...DEFAULT_STATE.mswMocks,
          ...(parsed.mswMocks ?? {}),
        },
      };
    }
  } catch {
    // ignore
  }

  return structuredClone(DEFAULT_STATE);
}

export function getPluginStorage<T>(key: string): T | null {
  try {
    return (loadState().plugins[key] as T) ?? null;
  } catch {
    return null;
  }
}

export function setPluginStorage<T>(key: string, data: T): void {
  updateState((s) => {
    s.plugins[key] = data;
  });
}

export function saveState(state: DevToolsStorage): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // ignore
  }
}

export function updateState(updater: (state: DevToolsStorage) => void): void {
  const state = loadState();
  updater(state);
  saveState(state);
}

export function exportState(): string {
  const state = loadState();
  return JSON.stringify({ ...state, panel: null }, null, 2);
}

export function importState(json: string): { success: true } | { success: false; error: string } {
  let parsed: unknown;

  try {
    parsed = JSON.parse(json);
  } catch {
    return { success: false, error: 'Invalid JSON file.' };
  }

  if (typeof parsed !== 'object' || parsed === null) {
    return { success: false, error: 'File does not contain a valid object.' };
  }

  const obj = parsed as Record<string, unknown>;

  if (obj['version'] !== '0.0.1') {
    return { success: false, error: `Unsupported version: "${obj['version']}". Expected "0.0.1".` };
  }

  if (obj['app'] !== 'b2b') {
    return { success: false, error: `Unsupported app: "${obj['app']}". Expected "b2b".` };
  }

  const partial = parsed as Partial<DevToolsStorage>;
  const newState: DevToolsStorage = {
    ...DEFAULT_STATE,
    ...partial,
    panel: loadState().panel,
    featureFlags: {
      ...DEFAULT_STATE.featureFlags,
      ...(partial.featureFlags ?? {}),
    },
    mswMocks: {
      ...DEFAULT_STATE.mswMocks,
      ...(partial.mswMocks ?? {}),
    },
  };

  saveState(newState);

  return { success: true };
}
